import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { Userpilot } from 'userpilot';
import { HttpClient } from '@angular/common/http';
import { DataLayerModel } from '../model/util.model';
import { ConfigService } from '../config/config.service';
import { all_category, CfgCategoryInfo, DEFAULT_HOMEPAGE } from '../config/config.model';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AnalyticsModel, AnalyticsService } from '@futura/futura-ui';
import { isDesktopDevice, isMobileDevice } from '../util/device.util';

declare const analytics: any;

@Injectable({
  providedIn: 'root',
})
export class UtilsService implements AnalyticsService {
  categories = new Array<CfgCategoryInfo>();

  public lastUrl?: string = '';

  constructor(
    private router: Router,
    private http: HttpClient,
    private configService: ConfigService,
    private gaService: GoogleAnalyticsService,
    private deviceService: DeviceDetectorService
  ) {
    configService.getUiConfig('_categories').subscribe(cats => (this.categories = cats));
  }

  public getFuturaVersion(): Observable<{ version: string }> {
    return this.http.get<{ version: string }>(`${environment.be_url}/version`);
  }

  public getVerticalName(): Observable<{ name: string }> {
    return this.http.get<{ name: string }>(`${environment.be_url}/vertical`);
  }

  public pushDataLayer(event: DataLayerModel): void {
    const provider = window as unknown as { dataLayer?: Array<unknown> };
    provider?.dataLayer?.push({ event: 'all_event', ...event });
  }

  public async gotoHomePage(queryParams: { [key: string]: string } = {}): Promise<boolean> {
    const homePage = await firstValueFrom(this.configService.getUiConfig('_homepage'));
    console.log({ homePage });
    return this.router.navigate([homePage || DEFAULT_HOMEPAGE], { queryParams });
  }

  public show500(): void {
    this.router.navigate(['/500']);
  }

  isMobileDevice(): boolean {
    return isMobileDevice();
  }

  isDesktopDevice(): boolean {
    return isDesktopDevice();
  }

  getMilestoneFromName(name: string): CfgCategoryInfo {
    for (const category of this.categories) {
      if (name.split('.')[0] == category.category_name) return category;
    }
    return all_category;
  }

  async sendEvent(
    eventName: string,
    eventParams: {
      [key: string]: any;
    } = {},
    useUserpilot = false,
    useSegment: boolean = true
  ) {
    eventParams['platform'] = environment.platform;
    eventParams['page'] = location.pathname;
    eventParams['query'] = location.href.split('?')[1];
    eventParams['device'] = this.deviceService.deviceType;

    const gtagData = JSON.parse(localStorage.getItem('gtagData') || '{}') || {};

    Object.entries(gtagData).forEach(([key, value]) => {
      if (!eventParams[key]) eventParams[key] = value;
    });

    this.gtag('event', (eventName || '').toLowerCase(), eventParams);
    if (useSegment) analytics.track(eventName, eventParams);
    if (useUserpilot) Userpilot.track(eventName, eventParams);
  }

  sendAnalytics(analytics: AnalyticsModel): void {
    const { eventName, eventParams } = analytics;
    this.sendEvent(eventName, eventParams);
  }

  public eventCategoriesFormatted(categories: Array<string> | string): { [key: string]: string } {
    const catArray = typeof categories === 'string' ? categories.split(',') : categories;

    let cat1 = new Array<string>();
    let cat2 = new Array<string>();
    let cat3 = new Array<string>();

    for (const cat of catArray) {
      const categorySplitted = cat.split('.');

      // if (categorySplitted.length > 0) cat1.push(categorySplitted[0]);
      // if (categorySplitted.length > 1) cat2.push(categorySplitted[1]);
      // if (categorySplitted.length > 2) cat3.push(categorySplitted[2]);

      if (categorySplitted[0] !== undefined) cat1.push(categorySplitted[0]);
      if (categorySplitted[1] !== undefined) cat2.push(categorySplitted[1]);
      if (categorySplitted[2] !== undefined) cat3.push(categorySplitted[2]);
    }

    cat1 = Array.from(new Set(cat1));
    cat2 = Array.from(new Set(cat2));
    cat3 = Array.from(new Set(cat3));
    const categoriesSet = Array.from(new Set(catArray));

    while (cat1.join(',').length > 255) cat1.pop();
    while (cat2.join(',').length > 255) cat2.pop();
    while (cat3.join(',').length > 255) cat3.pop();
    while (categoriesSet.join(',').length > 255) categoriesSet.pop();

    return {
      cat1: cat1.join(','),
      cat2: cat2.join(','),
      cat3: cat3.join(','),
      categories: categoriesSet.join(','),
    };
  }

  public gtag(
    eventType: string,
    eventName: string,
    eventParams: {
      [key: string]: any;
    } = {}
  ) {
    this.gaService.gtag(eventType, (eventName || '').toLowerCase(), eventParams);
  }
}
